import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axiosInstance from "../components/api/axiosInstance";
import PortalAlert from "./PortalAlert";
import { PROFILE_EDITABLE } from "./tenantProfileFields";
import { mobileValidationMessage, sanitizeMobileInput } from "./portalPhone";
import { updateClientPortalProfile } from "../utils/authStorage";

function buildFormFromProfile(data) {
  const initial = {};
  PROFILE_EDITABLE.forEach(([key]) => {
    initial[key] =
      key === "Alt_Phone" ? sanitizeMobileInput(data[key] || "") : data[key] || "";
  });
  return initial;
}

function buildProfilePayload(form) {
  const payload = {};
  PROFILE_EDITABLE.forEach(([key]) => {
    payload[key] = typeof form[key] === "string" ? form[key].trim() || null : form[key];
  });
  return payload;
}

function TenantProfileEdit() {
  const navigate = useNavigate();
  const [profile, setProfile] = useState(null);
  const [form, setForm] = useState({});
  const [loading, setLoading] = useState(true);
  const [savingProfile, setSavingProfile] = useState(false);
  const [profileMsg, setProfileMsg] = useState({ type: "", text: "" });

  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const res = await axiosInstance.get("/client/me");
        if (!active) return;
        const data = res.data?.data || {};
        setProfile(data);
        setForm(buildFormFromProfile(data));
      } catch (_) {
        if (active) setProfileMsg({ type: "error", text: "Could not load your profile." });
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => {
      active = false;
    };
  }, []);

  const setField = (name, value) => setForm((f) => ({ ...f, [name]: value }));

  const saveProfile = async (e) => {
    e.preventDefault();
    setProfileMsg({ type: "", text: "" });

    const altPhoneError = mobileValidationMessage(form.Alt_Phone, { label: "Alternate phone" });
    if (altPhoneError) {
      setProfileMsg({ type: "error", text: altPhoneError });
      return;
    }

    setSavingProfile(true);
    try {
      const res = await axiosInstance.put("/client/me", buildProfilePayload(form));
      const data = res.data?.data;
      if (data) {
        setProfile(data);
        try {
          updateClientPortalProfile(data);
        } catch (_) {}
      }
      setProfileMsg({ type: "success", text: "Profile updated." });
      setTimeout(() => navigate("/portal/profile"), 500);
    } catch (err) {
      setProfileMsg({ type: "error", text: err.response?.data?.message || "Could not update profile." });
    } finally {
      setSavingProfile(false);
    }
  };

  if (loading) {
    return <p className="cportal-muted">Loading…</p>;
  }

  return (
    <form className="cportal-card" onSubmit={saveProfile} noValidate>
      <div className="cportal-card-head">
        <h2>Edit Account Details</h2>
      </div>
      <PortalAlert
        type={profileMsg.type === "error" ? "error" : "success"}
        message={profileMsg.text}
        onClear={() => setProfileMsg({ type: "", text: "" })}
      />

      <h3 className="cportal-form-section">Contact</h3>
      <div className="cportal-form-grid cportal-form-grid--3">
        <div className="cportal-field">
          <label>Company Email (login)</label>
          <input value={profile?.Company_Email || profile?.Email || ""} disabled />
        </div>
        <div className="cportal-field">
          <label>Phone</label>
          <input value={profile?.Phone || ""} disabled />
        </div>
        {PROFILE_EDITABLE.map(([key, label]) => (
          <div className="cportal-field" key={key}>
            <label>{label}</label>
            {key === "Alt_Phone" ? (
              <input
                value={form[key] || ""}
                onChange={(e) => setField(key, sanitizeMobileInput(e.target.value))}
                inputMode="numeric"
                maxLength={10}
                placeholder="10-digit number"
              />
            ) : (
              <input value={form[key] || ""} onChange={(e) => setField(key, e.target.value)} />
            )}
          </div>
        ))}
      </div>

      <div className="cportal-form-actions">
        <button
          type="button"
          className="cportal-btn cportal-btn-ghost"
          onClick={() => navigate("/portal/profile")}
          disabled={savingProfile}
        >
          Cancel
        </button>
        <button type="submit" className="cportal-btn cportal-btn-primary" disabled={savingProfile}>
          {savingProfile ? "Saving…" : "Save changes"}
        </button>
      </div>
    </form>
  );
}

export default TenantProfileEdit;
